import * as React from 'react'
import { Focusable } from './Focusable'
import { Duration } from './Duration'
import { classNames } from './helpers'

export interface StreamListItem {
  /** Stable identity for the list key. */
  id: string;
  /** The stream's name — the button's primary label. */
  title: string;
  /** An optional second line under the title. Blank/absent renders no line. */
  description?: string | null;
  /** Length in seconds or an `HH:MM:SS` timecode. Absent renders no duration. */
  duration?: number | string | null;
  /**
   * The viewer's position in this stream, in seconds. When present and positive the
   * row shows it before the duration as a brand-coloured `position / duration`
   * timecode (e.g. `14:27 / 1:41:24`). For the loaded stream the consumer updates it
   * live as playback advances. Omitted for streams the viewer has never started.
   */
  position?: number | null;
}

/**
 * The playback state of the one row named by `activeId`, so the list can mirror the
 * player: `loading` waits on the source (spinner), `playing` offers to pause (pause
 * glyph), `paused` offers to resume (play glyph, but still the active row).
 */
export type StreamStatus = 'loading' | 'playing' | 'paused'

export interface StreamListProps {
  /** The streams on offer. An empty list renders nothing at all. */
  items: StreamListItem[];
  /** The section heading. Defaults to "Available streams". */
  heading?: string;
  /** Called when a stream is chosen (button click, or remote OK via Focusable). */
  onSelect?: (item: StreamListItem, index: number) => void;
  /**
   * The stream currently loaded in the player. Its row shows `status`; every other
   * row shows the plain play affordance. `null`/absent means nothing is playing.
   */
  activeId?: string | null;
  /** Playback state of the `activeId` row. Ignored for every other row. */
  status?: StreamStatus;
  className?: string;
}

/**
 * The play triangle. Decorative in every use — the button is already named by the
 * stream's title — so the wrapping element carries `aria-hidden`, not this.
 */
const PlayGlyph: React.FC = () => (
  <svg viewBox='0 0 24 24' focusable='false'><path d='M8 5v14l11-7z' fill='currentColor' /></svg>
)

/** The pause bars, shown on the active row while it is playing (the click pauses it). */
const PauseGlyph: React.FC = () => (
  <svg viewBox='0 0 24 24' focusable='false'><path d='M6 5h4v14H6zM14 5h4v14h-4z' fill='currentColor' /></svg>
)

/** A spinning ring while the active row's source is still being fetched/buffered. */
const Spinner: React.FC = () => (
  <svg className='mlsg-stream-list__spinner' viewBox='0 0 24 24' focusable='false'>
    <circle cx='12' cy='12' r='9' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeDasharray='44' strokeDashoffset='14' />
  </svg>
)

/** The glyph for a row, chosen from whether it is the active row and its status. */
const StreamIcon: React.FC<{ status?: StreamStatus }> = ({ status }) => {
  if (status === 'loading') return <Spinner />
  if (status === 'playing') return <PauseGlyph />
  return <PlayGlyph />
}

/**
 * The stream picker — the "Available streams" column beside a title: a heading and
 * a list of playable sources, each a focusable play button showing the stream's
 * name, an optional description, and its `Duration`.
 *
 * Every row is a real, focusable `<button>` wrapped in a `Focusable` stop, so the
 * list is navigable by remote as well as by pointer and keyboard. `onSelect` is
 * optional: without it the buttons render as inert-but-focusable choices — a
 * disabled button would instead drop out of the spatial-navigation tree.
 *
 * The list doubles as the player's transport: pass `activeId` (the loaded stream)
 * and `status`, and that row shows a spinner/pause/play and highlights itself, so a
 * viewer sees which stream is on and whether it is running without looking away
 * from the picker. The consumer decides what a click means (start another stream,
 * or toggle the active one) — this only reports the choice through `onSelect`.
 */
export function StreamList ({ items, heading = 'Available streams', onSelect, activeId, status, className }: StreamListProps) {
  const headingId = React.useId()
  if (items.length === 0) return null
  return (
    <div className={classNames('mlsg-stream-list', className)}>
      <h2 id={headingId} className='mlsg-stream-list__title'>{heading}</h2>
      <ul className='mlsg-stream-list__items' aria-labelledby={headingId}>
        {items.map((item, index) => {
          const isActive = activeId != null && item.id === activeId
          // Only the active row carries a status; a stale `status` must not colour
          // any other row when `activeId` moves on.
          const itemStatus = isActive ? status : undefined
          return (
            <li key={item.id} className='mlsg-stream-list__item'>
              <Focusable>
                <button
                  type='button'
                  className={classNames('mlsg-stream-list__button', isActive && 'mlsg-stream-list__button--active')}
                  // Active + playing, the click pauses — expose that toggle. Left off
                  // every other row, where the click just starts a stream.
                  aria-pressed={isActive ? itemStatus === 'playing' : undefined}
                  onClick={onSelect ? () => onSelect(item, index) : undefined}
                >
                  <span className={classNames('mlsg-stream-list__icon', isActive && 'mlsg-stream-list__icon--active')} aria-hidden='true'>
                    <StreamIcon status={itemStatus} />
                  </span>
                  <span className='mlsg-stream-list__label'>
                    <span className='mlsg-stream-list__name'>{item.title}</span>
                    {item.description?.trim()
                      ? <span className='mlsg-stream-list__description'>{item.description}</span>
                      : null}
                  </span>
                  {/* When the viewer has a position in this stream, show it before the
                      duration as a brand-lit `position / duration` timecode (the loaded
                      row updates it live); otherwise just the duration. */}
                  {item.position != null && item.position > 0
                    ? (
                      <span className='mlsg-stream-list__timecode'>
                        <Duration className='mlsg-stream-list__position' value={item.position} />
                        {item.duration != null
                          ? <> / <Duration value={item.duration} /></>
                          : null}
                      </span>
                      )
                    : item.duration != null
                      ? <Duration className='mlsg-stream-list__duration' value={item.duration} />
                      : null}
                </button>
              </Focusable>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
